/**
 * ÉN PERSONMODELL OG ÉN VELGER (arbeidspakken 22. oktober, A7 og A21; Atles svar 267).
 *
 * `GET /v1/people/search` søker på serveren i alt den som spør kjenner: kollegene, alle kontaktradene i
 * organisasjonen (også folk som bare står på en ting, `inRegister = false`), mine koblinger, og personene
 * i årgangen eller på programposten når kallestedet sier det. `POST /v1/people` lager en ny person med en
 * rad i organisasjonen, eller gir en kjent person (kollega, kobling) en rad. Ingen av dem gjør personen til
 * kontakt med mindre kalleren ber om det (`addToContacts`, A20).
 */
import { z } from 'zod';

import { localeSchema } from '../locale.js';
import { booleanQuerySchema } from './common.js';
import { contactVisibilitySchema } from './contacts.js';

/**
 * HVOR ET TREFF KOMMER FRA. En person kan ha flere: en kollega som også står på programposten.
 * `colleague`: medlem i organisasjonen jeg står i (eller en annen jeg er med i); `contact`: står i
 * Kontakter; `known`: har en rad her uten å stå i Kontakter (lagt på en ting); `mine`: min kobling;
 * `edition`/`performance`: står i årgangen eller på programposten kallestedet spør om.
 */
export const PERSON_SEARCH_SOURCES = [
  'colleague',
  'contact',
  'known',
  'mine',
  'edition',
  'performance',
] as const;
export type PersonSearchSource = (typeof PERSON_SEARCH_SOURCES)[number];

/** `org`, `edition:<uuid>` eller `performance:<uuid>`. */
export const personSearchScopeSchema = z
  .string()
  .regex(
    /^(org|edition:[0-9a-f-]{36}|performance:[0-9a-f-]{36})$/i,
    'Use org, edition:<id> or performance:<id>.',
  );
export type PersonSearchScope = z.infer<typeof personSearchScopeSchema>;

export const MAX_PERSON_SEARCH_HITS = 50;

export const peopleSearchQuerySchema = z
  .object({
    q: z.string().trim().max(120).optional(),
    scope: personSearchScopeSchema.default('org'),
    /** Bare personene i årgangen eller på programposten (vakt, ressurs, sjåfør). */
    onlyScope: booleanQuerySchema.optional(),
    /** Bare medlemmer av organisasjonen jeg står i (A22: kontaktpersoner hos festivalen). */
    membersOnly: booleanQuerySchema.optional(),
    limit: z.coerce.number().int().min(1).max(MAX_PERSON_SEARCH_HITS).default(20),
  })
  .meta({ id: 'PeopleSearchQuery' });

export const personSearchHitDtoSchema = z
  .object({
    personId: z.uuid(),
    /** Raden i organisasjonen jeg står i. Null for en kobling eller kollega som ikke har en ennå. */
    contactId: z.uuid().nullable(),
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    jobTitle: z.string().nullable(),
    companyName: z.string().nullable(),
    sources: z.array(z.enum(PERSON_SEARCH_SOURCES)),
    isMember: z.boolean(),
    hasLogin: z.boolean(),
    /** Null når personen ikke har en rad her. 'private' betyr min private kontakt (spør før deling). */
    visibility: contactVisibilitySchema.nullable(),
  })
  .meta({ id: 'PersonSearchHit' });

export const peopleSearchResponseSchema = z
  .object({ items: z.array(personSearchHitDtoSchema) })
  .meta({ id: 'PeopleSearchResponse' });

const draftName = z.string().trim().max(100).nullish();

/**
 * «+ NY PERSON» — ETT SKJEMA OVERALT. Fornavn, Etternavn, E-post og Telefon, og «Flere felt» for Tittel,
 * Firma, Språk og Notat. `displayName` virker for klienter med ett navnefelt. Minst ett navn.
 */
export const personDraftSchema = z
  .object({
    givenName: draftName,
    familyName: draftName,
    displayName: z.string().trim().max(200).nullish(),
    email: z.email().max(320).nullish(),
    phone: z.string().trim().max(40).nullish(),
    jobTitle: z.string().trim().max(200).nullish(),
    companyName: z.string().trim().max(200).nullish(),
    preferredLocale: localeSchema.nullish(),
    notes: z.string().max(10_000).nullish(),
  })
  .meta({ id: 'PersonDraft' });
export type PersonDraft = z.infer<typeof personDraftSchema>;

/** Navnet slik basen setter det sammen: «Fornavn Etternavn», ellers det hele navnet. */
export function personDraftName(draft: PersonDraft): string {
  const parts = `${(draft.givenName ?? '').trim()} ${(draft.familyName ?? '').trim()}`.trim();
  return parts !== '' ? parts : (draft.displayName ?? '').trim();
}

export const createPersonRequestSchema = personDraftSchema
  .extend({
    /** En kjent person (kollega, kobling) som skal få en rad her. Da brukes ikke navnefeltene. */
    personId: z.uuid().optional(),
    /** A20: personen blir kontakt bare når kalleren ber om det. Standard nei. */
    addToContacts: z.boolean().optional(),
    /** Bare med `addToContacts`: privat (standard) eller delt med organisasjonen (A2). */
    visibility: contactVisibilitySchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.personId === undefined && personDraftName(value) === '') {
      ctx.addIssue({ code: 'custom', path: ['givenName'], message: 'A person needs a name.' });
    }
  })
  .meta({ id: 'CreatePersonRequest' });

export const createPersonResponseSchema = z
  .object({ personId: z.uuid(), contactId: z.uuid() })
  .meta({ id: 'CreatePersonResponse' });

export type PeopleSearchQuery = z.infer<typeof peopleSearchQuerySchema>;
export type PersonSearchHitDto = z.infer<typeof personSearchHitDtoSchema>;
export type PeopleSearchResponse = z.infer<typeof peopleSearchResponseSchema>;
export type CreatePersonRequest = z.infer<typeof createPersonRequestSchema>;
export type CreatePersonResponse = z.infer<typeof createPersonResponseSchema>;
